import { CommonModule, formatDate } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router, RouterModule } from '@angular/router';
import { catchError, finalize, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { Company } from '../../company/company.model';
import { TenantScopeService } from '../../company/tenant-scope.service';
import { DriverService } from '../../fleet/driver/driver.service';
import { Driver } from '../../fleet/driver/driver.model';
import { MobilizationService } from '../../fleet/mobilization/mobilization.service';
import { FleetAvailabilityResponse, FleetAvailabilityRow } from '../../fleet/mobilization/models/mobilization.model';
import { VehicleService } from '../../fleet/vehicle/vehicle.service';
import { Vehicle } from '../../fleet/vehicle/vehicle.model';
import { CrmService } from '../../crm/crm.service';
import { APIListWrapper } from '../../models/common/api-list-wrapper.model';
import { City } from '../../models/common/city.model';
import { SessionStorageService } from '../../services/session-storage.service';
import { fleetResourceAgencyLabel, fleetResourceOwnerLabel, fleetResourceOwnershipSearchText, fleetResourceOwnershipTypeLabel, fleetResourceSourceCompanyLabel } from '../../shared/utils/fleet-resource-ownership.util';
import { isSisterCompanyResource, resolveSisterCompanyScope, sisterCompanyFleetAccessMessage, SisterCompanyScope } from '../../shared/utils/sister-company-scope.util';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { WarehousingMasterData } from '../../warehousing/warehousing.model';
import { WarehousingService } from '../../warehousing/warehousing.service';
import {
  TruckPlanBuildAssignment,
  TruckPlanBuildGroup,
  TruckPlanBuildPreview,
  TruckPlanReleaseCandidate
} from '../transport-planning.model';
import { TransportPlanningService } from '../transport-planning.service';

type ResourceViewMode = 'current' | 'sister';
type DriverOption = Driver & { optionLabel?: string; searchText?: string; tableRowKey?: string };
type VehicleOption = Vehicle & { optionLabel?: string; searchText?: string; tableRowKey?: string };
type MessageSeverity = 'info' | 'warning' | 'danger';

interface TruckPlanAssignmentLane {
  id: string;
  driver: DriverOption | null;
  vehicle: VehicleOption | null;
}

type RoutePointView = Pick<
  TruckPlanBuildGroup | TruckPlanReleaseCandidate,
  | 'originName'
  | 'originCityId'
  | 'originCityName'
  | 'originRegionName'
  | 'destinationName'
  | 'destinationCityId'
  | 'destinationCityName'
  | 'destinationRegionName'
>;

@Component({
  selector: 'app-transport-plan-create',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    CheckboxModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    MultiSelectModule,
    SelectButtonModule,
    SelectModule,
    TagModule,
    PageShellComponent,
    PageHeroComponent,
    PageDataPanelComponent
  ],
  templateUrl: './transport-plan-create.component.html',
  styleUrls: ['./transport-plan-create.component.css']
})
export class TransportPlanCreateComponent implements OnInit {
  preview: TruckPlanBuildPreview | null = null;
  selectedReleaseIds: number[] = [];
  planName = '';
  needFrom = '';
  needTo = '';
  isLoading = false;
  isCreating = false;
  assetsLoading = false;
  message = '';
  messageSeverity: MessageSeverity = 'info';
  resourceView: ResourceViewMode = 'current';
  sisterCompanyScope: SisterCompanyScope | null = null;
  selectedSisterCompanyId: number | null = null;
  availableDrivers: DriverOption[] = [];
  availableVehicles: VehicleOption[] = [];
  cityOptions: City[] = [];
  regionOptions: WarehousingMasterData[] = [];
  groupingModes: string[] = ['EXACT_LOCATION'];
  pickupCityIds: number[] = [];
  dropoffCityIds: number[] = [];
  pickupRegionIds: number[] = [];
  dropoffRegionIds: number[] = [];
  logisticsProjectId: number | null = null;
  globalDriver: DriverOption | null = null;
  globalVehicle: VehicleOption | null = null;

  readonly resourceViewOptions = [
    { label: 'Current Company', value: 'current' },
    { label: 'Sister Companies', value: 'sister' }
  ];

  readonly groupingModeOptions = [
    { label: 'Exact pickup and dropoff', value: 'EXACT_LOCATION' },
    { label: 'Pickup city', value: 'PICKUP_CITY' },
    { label: 'Dropoff city', value: 'DROPOFF_CITY' },
    { label: 'Pickup region', value: 'PICKUP_REGION' },
    { label: 'Dropoff region', value: 'DROPOFF_REGION' }
  ];

  private selectedReleases = new Map<string, Set<number>>();
  private releaseLane = new Map<number, string>();
  private groupLanes = new Map<string, TruckPlanAssignmentLane[]>();
  private driverAvailabilityByKey = new Map<string, FleetAvailabilityRow>();
  private vehicleAvailabilityByKey = new Map<string, FleetAvailabilityRow>();
  private laneSequence = 0;
  private scopeReady = false;
  private assetQueryVersion = 0;
  private readonly optionPageSize = 50;

  constructor(
    private service: TransportPlanningService,
    private driverService: DriverService,
    private vehicleService: VehicleService,
    private crmService: CrmService,
    private mobilizationService: MobilizationService,
    private warehousingService: WarehousingService,
    private tenantScopeService: TenantScopeService,
    private session: SessionStorageService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.selectedReleaseIds = this.parseReleaseIds(this.route.snapshot.queryParamMap.get('releaseIds'));
    this.groupingModes = this.parseGroupingModes(this.route.snapshot.queryParamMap.get('groupingModes'));
    this.logisticsProjectId = this.toOptionalNumber(this.route.snapshot.queryParamMap.get('logisticsProjectId'));
    this.applyLocationFilterRouteState(this.route.snapshot.queryParamMap);
    this.setDefaultWindow();
    this.loadPlanningFilterOptions();
    this.initializeSisterScope();
    this.loadPreview();
  }

  get currentCompanyId(): number | null {
    return this.session.getCompany()?.id ?? null;
  }

  get currentCompanyName(): string | null {
    return this.session.getCompany()?.name ?? null;
  }

  get sisterCompanies(): Company[] {
    return this.sisterCompanyScope?.sisterCompanies ?? [];
  }

  get sisterCompanyFilterOptions(): Array<{ id: number | null; name: string }> {
    return [
      { id: null, name: 'All sister companies' },
      ...this.sisterCompanies.map(company => ({
        id: company.id,
        name: company.name
      }))
    ];
  }

  get canUseSisterCompanyMode(): boolean {
    return !!this.sisterCompanyScope;
  }

  get isSisterMode(): boolean {
    return this.resourceView === 'sister' && this.canUseSisterCompanyMode;
  }

  loadPreview(): void {
    this.isLoading = true;
    this.clearMessage();
    this.service.buildPlanPreview({
      owningCompanyId: this.companyId(),
      releaseIds: this.selectedReleaseIds,
      needFrom: this.selectedReleaseIds.length ? null : this.dateFromInput(this.needFrom),
      needTo: this.selectedReleaseIds.length ? null : this.dateFromInput(this.needTo),
      logisticsProjectId: this.logisticsProjectId,
      groupingModes: this.groupingModes.length ? this.groupingModes : ['EXACT_LOCATION'],
      pickupCityIds: this.pickupCityIds,
      dropoffCityIds: this.dropoffCityIds,
      pickupRegionIds: this.pickupRegionIds,
      dropoffRegionIds: this.dropoffRegionIds
    }).subscribe({
      next: preview => {
        this.preview = preview;
        this.initializeGroupState();
        this.isLoading = false;
        this.maybeLoadAssets();
      },
      error: error => {
        this.setMessage(error?.error?.message || 'Could not load order releases for truck planning.', 'danger');
        this.isLoading = false;
      }
    });
  }

  createPlan(): void {
    const groups = this.assignmentGroups();
    if (!groups.length) {
      this.setMessage('Assign at least one selected release to a complete driver and vehicle lane.', 'warning');
      return;
    }
    if (this.hasIncompleteSelections()) {
      this.setMessage('Every selected release must be assigned to a lane with both driver and vehicle selected.', 'warning');
      return;
    }
    this.isCreating = true;
    this.clearMessage();
    this.service.createPlanFromSelection({
      owningCompanyId: this.companyId(),
      planName: this.planName.trim() || undefined,
      groups
    }).subscribe({
      next: response => {
        const planId = response?.plan?.planId;
        const text = `Truck plan created: ${response.includedReleaseCount} releases, ${response.routeCount} routes, ${response.ignoredReleaseCount} ignored.`;
        if (planId) {
          this.router.navigate(['/transport-planning/plans', planId], { state: { message: text } });
          return;
        }
        this.setMessage(text, 'info');
        this.isCreating = false;
      },
      error: error => {
        const errorMessage = error?.error?.message || 'Could not create truck plan. Review the selected assets and try again.';
        this.setMessage(errorMessage, this.isOverlapConflictMessage(errorMessage) ? 'warning' : 'danger');
        this.isCreating = false;
      }
    });
  }

  onResourceViewChange(value: ResourceViewMode): void {
    this.resourceView = value === 'sister' && this.canUseSisterCompanyMode ? 'sister' : 'current';
    this.globalDriver = null;
    this.globalVehicle = null;
    if (!this.isSisterMode) {
      this.selectedSisterCompanyId = null;
    }
    this.syncRouteState();
    this.loadAssets();
  }

  onSisterCompanyFilterChange(value: number | null): void {
    this.selectedSisterCompanyId = this.isValidSisterCompanyId(value) ? value : null;
    this.globalDriver = null;
    this.globalVehicle = null;
    this.syncRouteState();
    this.loadAssets();
  }

  onGroupingModesChange(values: string[] | null): void {
    this.groupingModes = values?.length ? [...values] : ['EXACT_LOCATION'];
    this.syncRouteState();
    this.loadPreview();
  }

  onLocationFiltersChange(): void {
    this.syncRouteState();
    this.loadPreview();
  }

  onGlobalDriverChange(): void {
    this.applyGlobalAssetsToAllLanes();
  }

  onGlobalVehicleChange(): void {
    this.applyGlobalAssetsToAllLanes();
  }

  groups(): TruckPlanBuildGroup[] {
    return (this.preview?.groups ?? [])
      .map(group => ({
        ...group,
        releases: group.releases ?? [],
        excludedReleases: group.excludedReleases ?? [],
        mobilizations: []
      }))
      .filter(group => group.releases.length > 0);
  }

  groupTitle(group: TruckPlanBuildGroup): string {
    return [
      this.date(group.needByDate),
      group.originName || 'Origin missing',
      'to',
      group.destinationName || 'Destination missing'
    ].join(' ');
  }

  groupMeta(group: TruckPlanBuildGroup): string {
    return [
      this.groupingModeLabel(group.groupingModes || this.groupingModes),
      group.vehicleTypeName || (group.vehicleTypeId ? `Vehicle type #${group.vehicleTypeId}` : 'Any truck type'),
      group.assignmentMode || 'OWN_TRUCK',
      group.releaseRole || 'DIRECT',
      `${group.releases?.length || 0} plannable`
    ].filter(Boolean).join(' | ');
  }

  groupingModeLabel(modes: string[]): string {
    const labels = (modes?.length ? modes : ['EXACT_LOCATION'])
      .map(mode => this.groupingModeOptions.find(option => option.value === mode)?.label)
      .filter(Boolean);
    return labels.length ? labels.join(' + ') : 'Exact pickup and dropoff';
  }

  cityLabel(city: City): string {
    return city?.cityName || `City #${city?.id || '-'}`;
  }

  regionLabel(region: WarehousingMasterData): string {
    return region?.name || region?.code || `Region #${region?.id || '-'}`;
  }

  pickupLocationLabel(routePoint: RoutePointView): string {
    return routePoint.originName || 'Pickup location missing';
  }

  dropoffLocationLabel(routePoint: RoutePointView): string {
    return routePoint.destinationName || 'Dropoff location missing';
  }

  pickupGeoLabel(routePoint: RoutePointView): string {
    return this.cityCountryLabel(routePoint.originCityId, routePoint.originCityName, routePoint.originRegionName);
  }

  dropoffGeoLabel(routePoint: RoutePointView): string {
    return this.cityCountryLabel(routePoint.destinationCityId, routePoint.destinationCityName, routePoint.destinationRegionName);
  }

  globalVehicleOptions(): VehicleOption[] {
    const groups = this.groups();
    if (!groups.length) {
      return this.availableVehicles;
    }
    return this.availableVehicles.filter(vehicle => groups.some(group => this.isVehicleCompatibleWithGroup(vehicle, group)));
  }

  globalAssignmentSummary(): string {
    if (!this.globalDriver && !this.globalVehicle) {
      return 'Choose a driver and truck once to apply them to every release group.';
    }
    const incompatibleGroups = this.globalVehicle
      ? this.groups().filter(group => !this.isVehicleCompatibleWithGroup(this.globalVehicle!, group)).length
      : 0;
    if (incompatibleGroups > 0) {
      return `${incompatibleGroups} group${incompatibleGroups === 1 ? '' : 's'} need a different truck type.`;
    }
    return 'Global driver and truck are applied to all current groups.';
  }

  isReleaseSelected(group: TruckPlanBuildGroup, release: TruckPlanReleaseCandidate): boolean {
    return !!release.releaseId && (this.selectedReleases.get(group.groupKey)?.has(release.releaseId) ?? false);
  }

  toggleRelease(group: TruckPlanBuildGroup, release: TruckPlanReleaseCandidate, checked: boolean): void {
    if (!release.releaseId) {
      return;
    }
    const selected = this.selectedReleasesFor(group);
    if (checked) {
      selected.add(release.releaseId);
      const firstLane = this.lanesFor(group)[0];
      if (firstLane) {
        this.releaseLane.set(release.releaseId, this.releaseLane.get(release.releaseId) ?? firstLane.id);
      }
      return;
    }
    selected.delete(release.releaseId);
    this.releaseLane.delete(release.releaseId);
  }

  lanesFor(group: TruckPlanBuildGroup): TruckPlanAssignmentLane[] {
    return this.groupLanes.get(group.groupKey) ?? [];
  }

  addLane(group: TruckPlanBuildGroup): void {
    const lanes = [...this.lanesFor(group), this.newLane()];
    this.groupLanes.set(group.groupKey, lanes);
  }

  removeLane(group: TruckPlanBuildGroup, lane: TruckPlanAssignmentLane): void {
    const remaining = this.lanesFor(group).filter(candidate => candidate.id !== lane.id);
    if (!remaining.length) {
      remaining.push(this.newLane());
    }
    this.groupLanes.set(group.groupKey, remaining);
    for (const releaseId of this.selectedReleasesFor(group)) {
      if (this.releaseLane.get(releaseId) === lane.id) {
        this.releaseLane.set(releaseId, remaining[0].id);
      }
    }
  }

  laneReleaseCount(group: TruckPlanBuildGroup, lane: TruckPlanAssignmentLane): number {
    let count = 0;
    for (const releaseId of this.selectedReleasesFor(group)) {
      if (this.releaseLane.get(releaseId) === lane.id) {
        count += 1;
      }
    }
    return count;
  }

  laneLabel(group: TruckPlanBuildGroup, lane: TruckPlanAssignmentLane, index: number): string {
    const title = `Lane ${index + 1}`;
    const parts = [
      lane.driver ? this.driverOptionLabel(lane.driver) : 'Choose driver',
      lane.vehicle ? this.vehicleOptionLabel(lane.vehicle) : 'Choose truck'
    ].filter(Boolean);
    return `${title} | ${parts.join(' / ')}`;
  }

  laneOptions(group: TruckPlanBuildGroup): Array<{ label: string; value: string }> {
    return this.lanesFor(group).map((lane, index) => ({
      label: this.laneLabel(group, lane, index),
      value: lane.id
    }));
  }

  releaseLaneId(release: TruckPlanReleaseCandidate): string | null {
    return release.releaseId ? this.releaseLane.get(release.releaseId) ?? null : null;
  }

  setReleaseLane(group: TruckPlanBuildGroup, release: TruckPlanReleaseCandidate, laneId: string | null): void {
    if (!release.releaseId) {
      return;
    }
    if (!laneId) {
      this.releaseLane.delete(release.releaseId);
      return;
    }
    if (this.lanesFor(group).some(lane => lane.id === laneId)) {
      this.releaseLane.set(release.releaseId, laneId);
    }
  }

  selectedLaneCount(group: TruckPlanBuildGroup): number {
    return this.lanesFor(group).filter(lane => this.laneReleaseCount(group, lane) > 0).length;
  }

  selectedReleaseCount(group: TruckPlanBuildGroup): number {
    return this.selectedReleases.get(group.groupKey)?.size ?? 0;
  }

  assignedReleaseCount(group: TruckPlanBuildGroup): number {
    let count = 0;
    for (const releaseId of this.selectedReleasesFor(group)) {
      const lane = this.findLaneById(group, this.releaseLane.get(releaseId) ?? null);
      if (lane && this.isLaneComplete(lane)) {
        count += 1;
      }
    }
    return count;
  }

  totalAssignedReleases(): number {
    return this.groups().reduce((total, group) => total + this.assignedReleaseCount(group), 0);
  }

  driverOptionsForGroup(_group: TruckPlanBuildGroup): DriverOption[] {
    return this.availableDrivers;
  }

  vehicleOptionsForGroup(group: TruckPlanBuildGroup): VehicleOption[] {
    return this.availableVehicles.filter(vehicle => this.isVehicleCompatibleWithGroup(vehicle, group));
  }

  onLaneDriverChange(group: TruckPlanBuildGroup, lane: TruckPlanAssignmentLane): void {
    this.tryAutoFillLaneVehicle(group, lane);
  }

  onLaneVehicleChange(group: TruckPlanBuildGroup, lane: TruckPlanAssignmentLane): void {
    if (!lane.vehicle) {
      return;
    }
    if (!this.isVehicleCompatibleWithGroup(lane.vehicle, group)) {
      lane.vehicle = null;
    }
  }

  driverAvailability(driver?: DriverOption | null): FleetAvailabilityRow | undefined {
    if (!driver) {
      return undefined;
    }
    return this.driverAvailabilityByKey.get(this.driverOptionKeyFromParts(driver.id ?? null, driver.resourceCompanyId ?? null));
  }

  vehicleAvailability(vehicle?: VehicleOption | null): FleetAvailabilityRow | undefined {
    if (!vehicle) {
      return undefined;
    }
    return this.vehicleAvailabilityByKey.get(
      this.vehicleOptionKeyFromParts(vehicle.vehicleId ?? null, vehicle.resourceCompanyId ?? null)
    );
  }

  availabilityStatusLabel(row?: FleetAvailabilityRow | null): string {
    switch (row?.availabilityStatus) {
      case 'FREE':
        return 'Idle';
      case 'BUSY':
        return 'Busy';
      case 'MAINTENANCE_BLOCKED':
        return 'Blocked';
      default:
        return 'Unknown';
    }
  }

  availabilitySeverity(row?: FleetAvailabilityRow | null): 'success' | 'warn' | 'danger' | 'info' {
    switch (row?.availabilityStatus) {
      case 'FREE':
        return 'success';
      case 'BUSY':
        return 'warn';
      case 'MAINTENANCE_BLOCKED':
        return 'danger';
      default:
        return 'info';
    }
  }

  availabilityLocationLine(row?: FleetAvailabilityRow | null): string {
    return row?.availableLocationLabel
      || [row?.availableCityName, row?.availableCountryName].filter(Boolean).join(', ')
      || 'Location unavailable';
  }

  availabilityNextLine(row?: FleetAvailabilityRow | null): string {
    if (!row) {
      return 'Availability pending';
    }
    if (row.nextFreeAt) {
      return `Next free ${this.availabilityDateLabel(row.nextFreeAt)}`;
    }
    return this.availabilityLocationLine(row);
  }

  availabilityFifoLine(row?: FleetAvailabilityRow | null): string {
    return row?.fifoRank != null ? `FIFO #${row.fifoRank}` : 'FIFO pending';
  }

  driverOwnerLine(driver?: DriverOption | null): string {
    if (!driver) {
      return '-';
    }
    return `${fleetResourceOwnershipTypeLabel(driver)} | ${fleetResourceOwnerLabel(driver)}`;
  }

  vehicleOwnerLine(vehicle?: VehicleOption | null): string {
    if (!vehicle) {
      return '-';
    }
    return `${fleetResourceOwnershipTypeLabel(vehicle)} | ${fleetResourceOwnerLabel(vehicle)}`;
  }

  driverAgencySourceLine(driver?: DriverOption | null): string {
    if (!driver) {
      return '-';
    }
    return [
      fleetResourceAgencyLabel(driver),
      fleetResourceSourceCompanyLabel(driver) || driver.resourceCompanyName || this.currentCompanyName
    ].filter(Boolean).join(' | ');
  }

  vehicleAgencySourceLine(vehicle?: VehicleOption | null): string {
    if (!vehicle) {
      return '-';
    }
    return [
      fleetResourceAgencyLabel(vehicle),
      fleetResourceSourceCompanyLabel(vehicle) || vehicle.resourceCompanyName || this.currentCompanyName
    ].filter(Boolean).join(' | ');
  }

  availabilitySummaryLine(row?: FleetAvailabilityRow | null): string {
    if (!row) {
      return 'Availability pending';
    }
    if (row.availabilityStatus === 'FREE') {
      return row.idleSince ? `Idle since ${this.availabilityDateLabel(row.idleSince)}` : 'Available now';
    }
    if (row.nextFreeAt) {
      return `Next free ${this.availabilityDateLabel(row.nextFreeAt)}`;
    }
    return row.availabilityReason || 'Busy on another assignment';
  }

  vehicleTypeLine(vehicle?: VehicleOption | null): string {
    return vehicle?.type?.name
      || 'Vehicle type not set';
  }

  vehicleIdentityLine(vehicle?: VehicleOption | null): string {
    return [vehicle?.plateNumber, vehicle?.registrationNumber].filter(Boolean).join(' | ') || 'Identity pending';
  }

  driverOptionLabel(driver?: DriverOption | null): string {
    if (!driver) {
      return 'Driver';
    }
    return driver.optionLabel || driver.legalName || driver.name || `Driver #${driver.id ?? ''}`.trim();
  }

  vehicleOptionLabel(vehicle?: VehicleOption | null): string {
    if (!vehicle) {
      return 'Vehicle';
    }
    return vehicle.optionLabel || vehicle.plateNumber || vehicle.registrationNumber || `Vehicle #${vehicle.vehicleId ?? ''}`.trim();
  }

  date(value?: string | Date): string {
    return value ? formatDate(value, 'dd MMM yyyy HH:mm', 'en-US') : 'Not set';
  }

  messageIcon(): string {
    switch (this.messageSeverity) {
      case 'warning':
        return 'pi pi-exclamation-triangle';
      case 'danger':
        return 'pi pi-times-circle';
      default:
        return 'pi pi-info-circle';
    }
  }

  shortDate(value?: string | Date): string {
    return value ? formatDate(value, 'dd MMM yyyy', 'en-US') : 'No date';
  }

  legLabel(row: TruckPlanReleaseCandidate): string {
    return row.legSequence && row.legCount ? `Leg ${row.legSequence} of ${row.legCount}` : (row.releaseRole || 'Direct');
  }

  private initializeSisterScope(): void {
    const applyScope = () => {
      this.sisterCompanyScope = resolveSisterCompanyScope(
        this.tenantScopeService.getAccessSummary(),
        this.currentCompanyId,
        ['fleet.drivers.view', 'fleet.vehicles.view']
      );
      this.applyRouteState(this.route.snapshot.queryParamMap);
      this.scopeReady = true;
      this.maybeLoadAssets();
    };

    this.tenantScopeService.initializeCurrentAccess().subscribe(() => applyScope());
  }

  private applyRouteState(queryParams: ParamMap): void {
    const requestedResourceView = queryParams.get('resourceView');
    this.resourceView = requestedResourceView === 'sister' && this.canUseSisterCompanyMode ? 'sister' : 'current';
    const requestedMemberCompanyId = this.toOptionalNumber(queryParams.get('memberCompanyId'));
    this.selectedSisterCompanyId = this.isValidSisterCompanyId(requestedMemberCompanyId) ? requestedMemberCompanyId : null;
    this.groupingModes = this.parseGroupingModes(queryParams.get('groupingModes'));
    this.logisticsProjectId = this.toOptionalNumber(queryParams.get('logisticsProjectId'));
    this.applyLocationFilterRouteState(queryParams);
  }

  private syncRouteState(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        releaseIds: this.selectedReleaseIds.length ? this.selectedReleaseIds.join(',') : null,
        logisticsProjectId: this.logisticsProjectId,
        resourceView: this.resourceView,
        memberCompanyId: this.isSisterMode ? this.selectedSisterCompanyId : null,
        groupingModes: this.groupingModes.join(','),
        pickupCityIds: this.pickupCityIds.length ? this.pickupCityIds.join(',') : null,
        dropoffCityIds: this.dropoffCityIds.length ? this.dropoffCityIds.join(',') : null,
        pickupRegionIds: this.pickupRegionIds.length ? this.pickupRegionIds.join(',') : null,
        dropoffRegionIds: this.dropoffRegionIds.length ? this.dropoffRegionIds.join(',') : null
      },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private maybeLoadAssets(): void {
    if (!this.scopeReady || !this.preview) {
      return;
    }
    this.loadAssets();
  }

  private loadPlanningFilterOptions(): void {
    forkJoin({
      // City options are intentionally left empty here: this page has no
      // country selector, so loading the world city directory would be unsafe.
      cities: of([] as City[]),
      regions: this.warehousingService.getMasterData('REGION', { activeStatus: 'ACTIVE' })
        .pipe(
          map(response => response.data || []),
          catchError(() => of([] as WarehousingMasterData[]))
        )
    }).subscribe(({ cities, regions }) => {
      this.cityOptions = [...cities].sort((left, right) => this.cityLabel(left).localeCompare(this.cityLabel(right)));
      this.regionOptions = [...regions].sort((left, right) => this.regionLabel(left).localeCompare(this.regionLabel(right)));
    });
  }

  private applyLocationFilterRouteState(queryParams: ParamMap): void {
    this.pickupCityIds = this.parseIdList(queryParams.get('pickupCityIds'));
    this.dropoffCityIds = this.parseIdList(queryParams.get('dropoffCityIds'));
    this.pickupRegionIds = this.parseIdList(queryParams.get('pickupRegionIds'));
    this.dropoffRegionIds = this.parseIdList(queryParams.get('dropoffRegionIds'));
  }

  private cityCountryLabel(cityId?: number | null, cityName?: string | null, regionName?: string | null): string {
    const city = cityId ? this.cityOptions.find(option => option.id === cityId) : undefined;
    const resolvedCityName = cityName || city?.cityName || null;
    const countryName = city?.country?.name || null;
    return [resolvedCityName, countryName].filter(Boolean).join(', ') || regionName || 'City and country not set';
  }

  private loadAssets(): void {
    if (!this.preview) {
      return;
    }
    const queryVersion = ++this.assetQueryVersion;
    this.assetsLoading = true;
    this.availableDrivers = [];
    this.availableVehicles = [];
    this.driverAvailabilityByKey.clear();
    this.vehicleAvailabilityByKey.clear();
    this.resetLaneAssets();
    forkJoin({
      drivers: this.fetchMixedDrivers(),
      vehicles: this.fetchMixedVehicles()
    }).pipe(
      switchMap(({ drivers, vehicles }) => {
        if (queryVersion !== this.assetQueryVersion) return of(null);
        this.availableDrivers = this.normalizeScopedDrivers(drivers);
        this.availableVehicles = this.normalizeScopedVehicles(vehicles);
        return this.loadScopedAvailability();
      }),
      finalize(() => {
        if (queryVersion === this.assetQueryVersion) this.assetsLoading = false;
      })
    ).subscribe({
      next: availability => {
        if (queryVersion !== this.assetQueryVersion) return;
        this.applyScopedAvailability(availability);
        this.reconcileGlobalAssets();
        this.applyGlobalAssetsToAllLanes();
      },
      error: error => {
        if (queryVersion !== this.assetQueryVersion) return;
        this.setMessage(error?.status === 403 && this.isSisterMode
          ? sisterCompanyFleetAccessMessage('both')
          : 'Unable to load scoped driver and vehicle assets.', 'danger');
      }
    });
  }

  private setMessage(message: string, severity: MessageSeverity): void {
    this.message = message;
    this.messageSeverity = severity;
  }

  private clearMessage(): void {
    this.message = '';
    this.messageSeverity = 'info';
  }

  private isOverlapConflictMessage(message?: string | null): boolean {
    return `${message || ''}`.toLowerCase().includes('overlapping truck plan route');
  }

  private fetchMixedDrivers(): Observable<Driver[]> {
    return forkJoin([
      this.fetchAllScopedDrivers('owned'),
      this.fetchAllScopedDrivers('freelance'),
      this.fetchAllScopedDrivers('rental'),
      this.fetchAllTransporterDrivers()
    ]).pipe(map(results => results.flat()));
  }

  private fetchMixedVehicles(): Observable<Vehicle[]> {
    return forkJoin([
      this.fetchAllScopedVehicles('owned'),
      this.fetchAllScopedVehicles('freelance'),
      this.fetchAllScopedVehicles('rental'),
      this.fetchAllTransporterVehicles()
    ]).pipe(map(results => results.flat()));
  }

  private fetchAllScopedDrivers(source: 'owned' | 'freelance' | 'rental', page = 0, collected: Driver[] = []): Observable<Driver[]> {
    return this.fetchScopedDriversPage(source, page).pipe(
      switchMap(response => {
        const nextItems = [...collected, ...(response.items ?? [])];
        return response.hasMore ? this.fetchAllScopedDrivers(source, page + 1, nextItems) : of(nextItems);
      })
    );
  }

  private fetchAllScopedVehicles(source: 'owned' | 'freelance' | 'rental', page = 0, collected: Vehicle[] = []): Observable<Vehicle[]> {
    return this.fetchScopedVehiclesPage(source, page).pipe(
      switchMap(response => {
        const nextItems = [...collected, ...(response.items ?? [])];
        return response.hasMore ? this.fetchAllScopedVehicles(source, page + 1, nextItems) : of(nextItems);
      })
    );
  }

  private fetchAllTransporterDrivers(): Observable<Driver[]> {
    const companyId = this.currentCompanyId;
    if (!companyId) return of([]);
    return this.crmService.getScopedTransporters(companyId).pipe(
      map(transporters => transporters.filter(transporter => !this.isSisterMode
        || isSisterCompanyResource(transporter.linkedCompanyId, this.sisterCompanyScope, this.selectedSisterCompanyId))),
      switchMap(transporters => transporters.length
        ? forkJoin(transporters.map(transporter => this.fetchAllTransporterDriversForClient(transporter.crmClientId)))
        : of([])),
      map(results => results.flat()),
      catchError(() => of([]))
    );
  }

  private fetchAllTransporterDriversForClient(transporterCrmClientId: number, page = 0, collected: Driver[] = []): Observable<Driver[]> {
    const companyId = this.currentCompanyId;
    if (!companyId) return of(collected);
    return this.driverService.getTransporterDrivers(page, this.optionPageSize, companyId, transporterCrmClientId).pipe(
      switchMap(response => {
        const nextItems = [...collected, ...(response.items ?? [])];
        return response.hasMore
          ? this.fetchAllTransporterDriversForClient(transporterCrmClientId, page + 1, nextItems)
          : of(nextItems);
      }),
      catchError(() => of(collected))
    );
  }

  private fetchAllTransporterVehicles(): Observable<Vehicle[]> {
    const companyId = this.currentCompanyId;
    if (!companyId) return of([]);
    return this.crmService.getScopedTransporters(companyId).pipe(
      map(transporters => transporters.filter(transporter => !this.isSisterMode
        || isSisterCompanyResource(transporter.linkedCompanyId, this.sisterCompanyScope, this.selectedSisterCompanyId))),
      switchMap(transporters => transporters.length
        ? forkJoin(transporters.map(transporter => this.fetchAllTransporterVehiclesForClient(transporter.crmClientId)))
        : of([])),
      map(results => results.flat()),
      catchError(() => of([]))
    );
  }

  private fetchAllTransporterVehiclesForClient(transporterCrmClientId: number, page = 0, collected: Vehicle[] = []): Observable<Vehicle[]> {
    const companyId = this.currentCompanyId;
    if (!companyId) return of(collected);
    return this.vehicleService.getTransporterVehicles(page, this.optionPageSize, companyId, transporterCrmClientId).pipe(
      switchMap(response => {
        const nextItems = [...collected, ...(response.items ?? [])];
        return response.hasMore
          ? this.fetchAllTransporterVehiclesForClient(transporterCrmClientId, page + 1, nextItems)
          : of(nextItems);
      }),
      catchError(() => of(collected))
    );
  }

  private fetchScopedDriversPage(source: 'owned' | 'freelance' | 'rental', page: number): Observable<APIListWrapper<Driver>> {
    const companyId = this.currentCompanyId;
    if (!companyId) {
      return of(this.emptyListWrapper<Driver>());
    }
    if (this.isSisterMode) {
      if (!this.sisterCompanyScope) {
        return of(this.emptyListWrapper<Driver>());
      }
      if (source === 'rental') {
        return this.driverService.getRentalDriversByCompanyGroup(
          page,
          this.optionPageSize,
          this.sisterCompanyScope.companyGroupId,
          companyId,
          this.selectedSisterCompanyId
        );
      }
      if (source === 'freelance') {
        return this.driverService.getFreelanceDriversByCompanyGroup(
          page,
          this.optionPageSize,
          this.sisterCompanyScope.companyGroupId,
          companyId,
          this.selectedSisterCompanyId
        );
      }
      return this.driverService.findDriversByCompanyGroup(
        page,
        this.optionPageSize,
        this.sisterCompanyScope.companyGroupId,
        companyId,
        this.selectedSisterCompanyId
      );
    }

    if (source === 'rental') {
      return this.driverService.getRentalDriversByCompanyId(page, this.optionPageSize, companyId);
    }
    if (source === 'freelance') {
      return this.driverService.getFreelanceDriversByCompanyId(page, this.optionPageSize, companyId);
    }
    const agencyId = this.session.getCompany()?.transportationAgencyId;
    if (!agencyId) {
      return of(this.emptyListWrapper<Driver>());
    }
    return this.driverService.findDriversByTransportationAgencyId(page, this.optionPageSize, agencyId);
  }

  private fetchScopedVehiclesPage(source: 'owned' | 'freelance' | 'rental', page: number): Observable<APIListWrapper<Vehicle>> {
    const companyId = this.currentCompanyId;
    if (!companyId) {
      return of(this.emptyListWrapper<Vehicle>());
    }
    if (this.isSisterMode) {
      if (!this.sisterCompanyScope) {
        return of(this.emptyListWrapper<Vehicle>());
      }
      if (source === 'rental') {
        return this.vehicleService.getRentalVehiclesByCompanyGroup(
          page,
          this.optionPageSize,
          this.sisterCompanyScope.companyGroupId,
          companyId,
          this.selectedSisterCompanyId
        );
      }
      if (source === 'freelance') {
        return this.vehicleService.getFreelanceVehiclesByCompanyGroup(
          page,
          this.optionPageSize,
          this.sisterCompanyScope.companyGroupId,
          companyId,
          this.selectedSisterCompanyId
        );
      }
      return this.vehicleService.findVehiclesByCompanyGroup(
        page,
        this.optionPageSize,
        this.sisterCompanyScope.companyGroupId,
        companyId,
        this.selectedSisterCompanyId
      );
    }

    if (source === 'rental') {
      return this.vehicleService.getRentalVehiclesByCompanyId(page, this.optionPageSize, companyId);
    }
    if (source === 'freelance') {
      return this.vehicleService.getFreelanceVehiclesByCompanyId(page, this.optionPageSize, companyId);
    }
    const agencyId = this.session.getCompany()?.transportationAgencyId;
    if (!agencyId) {
      return of(this.emptyListWrapper<Vehicle>());
    }
    return this.vehicleService.findVehiclesByTransportationAgencyId(page, this.optionPageSize, agencyId);
  }

  private loadScopedAvailability(): Observable<FleetAvailabilityResponse | null> {
    const { dateFrom, dateTo } = this.assetWindow();
    if (this.isSisterMode && this.sisterCompanyScope && this.currentCompanyId) {
      return this.mobilizationService.getAvailabilityByCompanyGroup({
        companyGroupId: this.sisterCompanyScope.companyGroupId,
        excludeCompanyId: this.currentCompanyId,
        memberCompanyId: this.selectedSisterCompanyId,
        dateFrom,
        dateTo
      }).pipe(
        map(response => response.data ?? null)
      );
    }
    if (!this.currentCompanyId) {
      return of(null);
    }
    return this.mobilizationService.getAvailability({
      owningCompanyId: this.currentCompanyId,
      dateFrom,
      dateTo
    }).pipe(
      map(response => response.data ?? null)
    );
  }

  private normalizeScopedDrivers(drivers: Driver[]): DriverOption[] {
    const seen = new Set<string>();
    return drivers
      .filter(driver => !this.isSisterMode || isSisterCompanyResource(driver.resourceCompanyId, this.sisterCompanyScope, this.selectedSisterCompanyId))
      .map(driver => this.normalizeDriverOption(driver))
      .filter(driver => {
        const key = driver.tableRowKey || '';
        if (seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      })
      .sort((left, right) => this.compareDriverOptions(left, right));
  }

  private normalizeScopedVehicles(vehicles: Vehicle[]): VehicleOption[] {
    const seen = new Set<string>();
    return vehicles
      .filter(vehicle => !this.isSisterMode || isSisterCompanyResource(vehicle.resourceCompanyId, this.sisterCompanyScope, this.selectedSisterCompanyId))
      .map(vehicle => this.normalizeVehicleOption(vehicle))
      .filter(vehicle => {
        const key = vehicle.tableRowKey || '';
        if (seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      })
      .sort((left, right) => this.compareVehicleOptions(left, right));
  }

  private normalizeDriverOption(driver: Driver): DriverOption {
    const resourceCompanyId = driver.resourceCompanyId ?? this.currentCompanyId ?? undefined;
    const resourceCompanyName = driver.resourceCompanyName ?? this.currentCompanyName ?? undefined;
    return {
      ...driver,
      resourceCompanyId,
      resourceCompanyName,
      optionLabel: driver.legalName || driver.name || `Driver #${driver.id ?? ''}`.trim(),
      searchText: [
        driver.legalName,
        driver.name,
        driver.phoneNumber,
        fleetResourceOwnershipSearchText({ ...driver, resourceCompanyName })
      ].filter(Boolean).join(' '),
      tableRowKey: this.driverOptionKeyFromParts(driver.id ?? null, resourceCompanyId ?? null)
    };
  }

  private normalizeVehicleOption(vehicle: Vehicle): VehicleOption {
    const resourceCompanyId = vehicle.resourceCompanyId ?? this.currentCompanyId ?? undefined;
    const resourceCompanyName = vehicle.resourceCompanyName ?? this.currentCompanyName ?? undefined;
    return {
      ...vehicle,
      resourceCompanyId,
      resourceCompanyName,
      optionLabel: vehicle.plateNumber || vehicle.registrationNumber || `Vehicle #${vehicle.vehicleId ?? ''}`.trim(),
      searchText: [
        vehicle.plateNumber,
        vehicle.registrationNumber,
        vehicle.engineNumber,
        fleetResourceOwnershipSearchText({ ...vehicle, resourceCompanyName })
      ].filter(Boolean).join(' '),
      tableRowKey: this.vehicleOptionKeyFromParts(vehicle.vehicleId ?? null, resourceCompanyId ?? null)
    };
  }

  private applyScopedAvailability(availability: FleetAvailabilityResponse | null): void {
    this.driverAvailabilityByKey.clear();
    this.vehicleAvailabilityByKey.clear();

    for (const row of availability?.driverRows ?? []) {
      this.driverAvailabilityByKey.set(
        this.driverOptionKeyFromParts(row.resourceId ?? null, row.resourceCompanyId ?? null),
        row
      );
    }
    for (const row of availability?.vehicleRows ?? []) {
      this.vehicleAvailabilityByKey.set(
        this.vehicleOptionKeyFromParts(row.resourceId ?? null, row.resourceCompanyId ?? null),
        row
      );
    }

    this.availableDrivers = [...this.availableDrivers].sort((left, right) => this.compareDriverOptions(left, right));
    this.availableVehicles = [...this.availableVehicles].sort((left, right) => this.compareVehicleOptions(left, right));
  }

  private initializeGroupState(): void {
    this.selectedReleases.clear();
    this.releaseLane.clear();
    this.groupLanes.clear();
    this.laneSequence = 0;
    for (const group of this.groups()) {
      const lane = this.newLane();
      this.groupLanes.set(group.groupKey, [lane]);
      const selected = this.selectedReleasesFor(group);
      for (const release of group.releases ?? []) {
        if (!release.releaseId) {
          continue;
        }
        selected.add(release.releaseId);
        this.releaseLane.set(release.releaseId, lane.id);
      }
    }
  }

  private resetLaneAssets(): void {
    for (const lanes of this.groupLanes.values()) {
      lanes.forEach(lane => {
        lane.driver = null;
        lane.vehicle = null;
      });
    }
  }

  private reconcileGlobalAssets(): void {
    if (this.globalDriver) {
      const key = this.globalDriver.tableRowKey;
      this.globalDriver = this.availableDrivers.find(driver => driver.tableRowKey === key) ?? null;
    }
    if (this.globalVehicle) {
      const key = this.globalVehicle.tableRowKey;
      this.globalVehicle = this.availableVehicles.find(vehicle => vehicle.tableRowKey === key) ?? null;
    }
  }

  private applyGlobalAssetsToAllLanes(): void {
    if (!this.preview || (!this.globalDriver && !this.globalVehicle)) {
      return;
    }
    for (const group of this.groups()) {
      const lanes = this.lanesFor(group);
      const lane = lanes[0] ?? this.newLane();
      if (!lanes.length) {
        this.groupLanes.set(group.groupKey, [lane]);
      }
      if (this.globalDriver) {
        lane.driver = this.globalDriver;
      }
      if (this.globalVehicle) {
        lane.vehicle = this.isVehicleCompatibleWithGroup(this.globalVehicle, group) ? this.globalVehicle : null;
      }
      for (const release of group.releases ?? []) {
        if (!release.releaseId) {
          continue;
        }
        this.selectedReleasesFor(group).add(release.releaseId);
        this.releaseLane.set(release.releaseId, lane.id);
      }
    }
  }

  private newLane(): TruckPlanAssignmentLane {
    this.laneSequence += 1;
    return {
      id: `lane-${this.laneSequence}`,
      driver: null,
      vehicle: null
    };
  }

  private tryAutoFillLaneVehicle(group: TruckPlanBuildGroup, lane: TruckPlanAssignmentLane): void {
    const currentVehicleId = lane.driver?.currentVehicle?.vehicleId;
    if (!currentVehicleId) {
      if (lane.vehicle && !this.isVehicleCompatibleWithGroup(lane.vehicle, group)) {
        lane.vehicle = null;
      }
      return;
    }
    const compatibleVehicles = this.vehicleOptionsForGroup(group);
    const exactMatches = compatibleVehicles.filter(vehicle =>
      vehicle.vehicleId === currentVehicleId
      && vehicle.resourceCompanyId === (lane.driver?.resourceCompanyId ?? null)
    );
    if (exactMatches.length === 1) {
      lane.vehicle = exactMatches[0];
      return;
    }
    const uniqueMatches = compatibleVehicles.filter(vehicle => vehicle.vehicleId === currentVehicleId);
    if (uniqueMatches.length === 1) {
      lane.vehicle = uniqueMatches[0];
      return;
    }
    if (!lane.vehicle || lane.vehicle.vehicleId !== currentVehicleId) {
      lane.vehicle = null;
    }
  }

  private assignmentGroups(): Array<{ groupKey: string; assignments: TruckPlanBuildAssignment[] }> {
    const groups: Array<{ groupKey: string; assignments: TruckPlanBuildAssignment[] }> = [];
    for (const group of this.groups()) {
      const assignments = this.lanesFor(group)
        .map(lane => {
          const releaseIds = Array.from(this.selectedReleasesFor(group))
            .filter(releaseId => this.releaseLane.get(releaseId) === lane.id);
          if (!releaseIds.length || !lane.driver?.id || !lane.vehicle?.vehicleId) {
            return null;
          }
          return {
            driverId: Number(lane.driver.id),
            driverResourceCompanyId: lane.driver.resourceCompanyId ?? null,
            vehicleId: Number(lane.vehicle.vehicleId),
            vehicleResourceCompanyId: lane.vehicle.resourceCompanyId ?? null,
            transporterCrmClientId: lane.driver.transporterCrmClientId
              ?? lane.vehicle.transporterCrmClientId
              ?? null,
            releaseIds
          } as TruckPlanBuildAssignment;
        })
        .filter((assignment): assignment is TruckPlanBuildAssignment => !!assignment);
      if (assignments.length) {
        groups.push({ groupKey: group.groupKey, assignments });
      }
    }
    return groups;
  }

  private hasIncompleteSelections(): boolean {
    for (const group of this.groups()) {
      for (const releaseId of this.selectedReleasesFor(group)) {
        const lane = this.findLaneById(group, this.releaseLane.get(releaseId) ?? null);
        if (!lane || !this.isLaneComplete(lane)) {
          return true;
        }
      }
    }
    return false;
  }

  private isLaneComplete(lane: TruckPlanAssignmentLane): boolean {
    return !!lane.driver?.id && !!lane.vehicle?.vehicleId;
  }

  private compareDriverOptions(left: DriverOption, right: DriverOption): number {
    return this.compareAvailabilityRowsForOptions(
      this.driverAvailability(left),
      this.driverAvailability(right),
      this.driverOptionLabel(left),
      this.driverOptionLabel(right)
    );
  }

  private compareVehicleOptions(left: VehicleOption, right: VehicleOption): number {
    return this.compareAvailabilityRowsForOptions(
      this.vehicleAvailability(left),
      this.vehicleAvailability(right),
      this.vehicleOptionLabel(left),
      this.vehicleOptionLabel(right)
    );
  }

  private compareAvailabilityRowsForOptions(
    left: FleetAvailabilityRow | undefined,
    right: FleetAvailabilityRow | undefined,
    leftLabel: string,
    rightLabel: string
  ): number {
    const statusDelta = this.availabilityRank(left) - this.availabilityRank(right);
    if (statusDelta !== 0) {
      return statusDelta;
    }
    const fifoDelta = this.fifoRankValue(left) - this.fifoRankValue(right);
    if (fifoDelta !== 0) {
      return fifoDelta;
    }
    return leftLabel.localeCompare(rightLabel);
  }

  private availabilityRank(row?: FleetAvailabilityRow): number {
    switch (row?.availabilityStatus) {
      case 'FREE':
        return 0;
      case 'BUSY':
        return 1;
      case 'MAINTENANCE_BLOCKED':
        return 2;
      default:
        return 3;
    }
  }

  private fifoRankValue(row?: FleetAvailabilityRow): number {
    return row?.fifoRank ?? Number.MAX_SAFE_INTEGER;
  }

  private assetWindow(): { dateFrom?: Date; dateTo?: Date } {
    const groupStarts = this.groups()
      .map(group => group.windowStart ? new Date(group.windowStart) : null)
      .filter((value): value is Date => !!value && !Number.isNaN(value.getTime()));
    const groupEnds = this.groups()
      .map(group => group.windowEnd ? new Date(group.windowEnd) : null)
      .filter((value): value is Date => !!value && !Number.isNaN(value.getTime()));
    return {
      dateFrom: groupStarts.length ? new Date(Math.min(...groupStarts.map(value => value.getTime()))) : (this.dateFromInput(this.needFrom) ?? undefined),
      dateTo: groupEnds.length ? new Date(Math.max(...groupEnds.map(value => value.getTime()))) : (this.dateFromInput(this.needTo) ?? undefined)
    };
  }

  private isVehicleCompatibleWithGroup(vehicle: VehicleOption, group: TruckPlanBuildGroup): boolean {
    const requiredTypeId = group.vehicleTypeId ?? null;
    const actualTypeId = vehicle.type?.id ?? null;
    return requiredTypeId == null || actualTypeId == null || requiredTypeId === actualTypeId;
  }

  private findLaneById(group: TruckPlanBuildGroup, laneId: string | null): TruckPlanAssignmentLane | undefined {
    return laneId ? this.lanesFor(group).find(lane => lane.id === laneId) : undefined;
  }

  private selectedReleasesFor(group: TruckPlanBuildGroup): Set<number> {
    if (!this.selectedReleases.has(group.groupKey)) {
      this.selectedReleases.set(group.groupKey, new Set<number>());
    }
    return this.selectedReleases.get(group.groupKey)!;
  }

  private driverOptionKeyFromParts(driverId: number | null, resourceCompanyId: number | null): string {
    return `${driverId ?? 'none'}:${resourceCompanyId ?? 'none'}`;
  }

  private vehicleOptionKeyFromParts(vehicleId: number | null, resourceCompanyId: number | null): string {
    return `${vehicleId ?? 'none'}:${resourceCompanyId ?? 'none'}`;
  }

  private emptyListWrapper<T>(): APIListWrapper<T> {
    return {
      items: [],
      count: 0,
      hasMore: false,
      limit: 0,
      offset: 0
    } as APIListWrapper<T>;
  }

  private availabilityDateLabel(value?: string | Date | number | null): string {
    if (!value) {
      return '-';
    }
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
  }

  private isValidSisterCompanyId(value: number | null | undefined): value is number {
    return !!value && this.sisterCompanies.some(company => company.id === value);
  }

  private toOptionalNumber(value: string | null): number | null {
    if (!value) {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  private parseReleaseIds(value: string | null): number[] {
    return this.parseIdList(value);
  }

  private parseIdList(value: string | null): number[] {
    if (!value) {
      return [];
    }
    return value.split(',')
      .map(part => Number(part.trim()))
      .filter(id => Number.isFinite(id) && id > 0);
  }

  private parseGroupingModes(value: string | null): string[] {
    const supported = new Set(this.groupingModeOptions.map(option => option.value));
    const modes = (value || '')
      .split(',')
      .flatMap(part => {
        const mode = part.trim().toUpperCase();
        if (mode === 'CITY') return ['PICKUP_CITY', 'DROPOFF_CITY'];
        if (mode === 'REGION') return ['PICKUP_REGION', 'DROPOFF_REGION'];
        return [mode];
      })
      .filter(mode => supported.has(mode));
    return modes.length ? Array.from(new Set(modes)) : ['EXACT_LOCATION'];
  }

  private setDefaultWindow(): void {
    const start = new Date();
    const end = new Date(start.getTime() + 14 * 24 * 60 * 60 * 1000);
    this.needFrom = this.inputDate(start);
    this.needTo = this.inputDate(end);
  }

  private inputDate(value: Date): string {
    const pad = (part: number) => String(part).padStart(2, '0');
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}T${pad(value.getHours())}:${pad(value.getMinutes())}`;
  }

  private dateFromInput(value: string): Date | null {
    if (!value) {
      return null;
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private companyId(): number {
    return this.currentCompanyId ?? 0;
  }
}
